
import { useEffect, useRef, useState } from 'react';
import { realtimeService } from '@/services/realtime/RealtimeService';

interface UseCollectionRealtimeProps {
  onItemAdded?: (item: any) => void;
  onItemUpdated?: (item: any) => void;
  onItemRemoved?: (itemId: string) => void;
  onError?: (error: any) => void;
}

export function useCollectionRealtime(
  collectionType: string,
  filters: Record<string, any>,
  handlers: UseCollectionRealtimeProps,
  enabled: boolean = true
) {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');
  const [lastEvent, setLastEvent] = useState<any>(null);
  const [lastEventTime, setLastEventTime] = useState<Date | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    console.log(`[useCollectionRealtime] Setting up subscription for ${collectionType}:`, filters);
    setConnectionStatus('connecting');

    // Determine table configuration based on collection type
    let tableConfigs = [];
    
    if (collectionType === 'comments') {
      // Handle both card and poll comments
      if (filters.cardId) {
        tableConfigs.push({
          table: 'card_comments',
          event: '*' as const,
          filter: `card_id=eq.${filters.cardId}`
        });
      }
      if (filters.pollId) {
        tableConfigs.push({
          table: 'poll_comments', 
          event: '*' as const,
          filter: `poll_id=eq.${filters.pollId}`
        });
      }
      
      // Add parent filter if specified
      if (filters.parentId) {
        tableConfigs = tableConfigs.map(config => ({
          ...config,
          filter: `${config.filter} AND parent_id=eq.${filters.parentId}`
        }));
      }
    }

    if (tableConfigs.length === 0) {
      console.warn(`[useCollectionRealtime] No table configuration found for ${collectionType}`);
      return;
    }

    // Set up the subscription
    const unsubscribe = realtimeService.subscribeToCollection(
      {
        collectionType,
        tables: tableConfigs,
        filters
      },
      {
        onInsert: (payload) => {
          console.log(`[useCollectionRealtime] INSERT event:`, payload);
          setLastEvent(payload);
          setLastEventTime(new Date());
          handlers.onItemAdded?.(payload.new);
        },
        onUpdate: (payload) => {
          console.log(`[useCollectionRealtime] UPDATE event:`, payload);
          setLastEvent(payload);
          setLastEventTime(new Date());
          handlers.onItemUpdated?.(payload.new);
        },
        onDelete: (payload) => {
          console.log(`[useCollectionRealtime] DELETE event:`, payload);
          setLastEvent(payload);
          setLastEventTime(new Date());
          handlers.onItemRemoved?.(payload.old?.id);
        },
        onError: (error) => {
          console.error(`[useCollectionRealtime] ERROR:`, error);
          setConnectionStatus('error');
          setIsConnected(false);
          handlers.onError?.(error);
        }
      }
    );

    unsubscribeRef.current = unsubscribe;

    // Simulate connection success after a brief delay
    const timer = setTimeout(() => {
      setConnectionStatus('connected');
      setIsConnected(true);
    }, 1000);

    return () => {
      clearTimeout(timer);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      setIsConnected(false);
      setConnectionStatus('disconnected');
    };
  }, [collectionType, JSON.stringify(filters), enabled]);

  return {
    isConnected,
    connectionStatus,
    lastEvent,
    lastEventTime
  };
}
